import { Divider, Skeleton } from '@design-system';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { ProductPrice } from '../../../../components/ProductPrice/ProductPrice';
import {
  GeneratedMoneyInput,
  GeneratedOrderPricingQuote,
  GeneratedTaxType,
} from '../../../../types/generated';
import { CurrencyCode } from '../../../../types/Locations';

interface ReviewPricingInformation {
  paymentReady: boolean;
  payWithCrypto: boolean;
  isPreOrder?: boolean;
  pricingBreakdown: GeneratedOrderPricingQuote;
  updating: boolean;
  bid?: GeneratedMoneyInput | null;
}

export const ReviewPricingInformation: React.FC<ReviewPricingInformation> = ({
  bid,
  isPreOrder = false,
  payWithCrypto,
  paymentReady = false,
  pricingBreakdown,
  updating,
}) => {
  const { pricing } = pricingBreakdown;
  const { includesCalculatedShipping } = pricing;
  const { t } = useTranslation(['product', 'countries']);

  return (
    <div className="flex flex-col space-y-1.5">
      {bid && (
        <>
          <div className="flex justify-between">
            <div>
              {t('product:drawers.review.breakdown.bid')}
              {pricing.taxType === GeneratedTaxType.Vat
                ? ` (${t('product:drawers.review.breakdown.noVAT')})`
                : null}
            </div>
            <div>
              {updating ? (
                <Skeleton className="h-full w-24" />
              ) : (
                <ProductPrice
                  currencyCode={pricing.currencyCode as CurrencyCode}
                  price={Number(bid.amount)}
                  skipConvertCurrency
                  skipTaxCalculation
                />
              )}
            </div>
          </div>
          <Divider variant="dashed" />
        </>
      )}
      <div className="flex justify-between">
        <div>{t('product:drawers.review.breakdown.subTotal')}</div>
        <div>
          {updating ? (
            <Skeleton className="h-full w-24" />
          ) : (
            <ProductPrice
              currencyCode={pricing.currencyCode as CurrencyCode}
              inMinorUnit
              price={pricing.subtotalItemsPrice}
              skipConvertCurrency
            />
          )}
        </div>
      </div>
      <div className="flex justify-between">
        <div>{t('product:drawers.review.breakdown.shipping')}</div>
        <div>
          {updating ? (
            <Skeleton className="h-full w-20" />
          ) : (
            <>
              {includesCalculatedShipping ? (
                <ProductPrice
                  currencyCode={pricing.currencyCode as CurrencyCode}
                  inMinorUnit
                  price={pricing.subtotalShippingPrice}
                  skipConvertCurrency
                />
              ) : (
                <span className="text-neutral-4">
                  {t('product:drawers.review.breakdown.addAddressToCalculate')}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      {pricing.taxType === GeneratedTaxType.Ddp ? (
        <div className="flex justify-between">
          <div>{t('drawers.review.breakdown.duty')}</div>
          <div>
            {updating ? (
              <Skeleton className="h-full w-20" />
            ) : (
              <ProductPrice
                currencyCode={pricing.currencyCode as CurrencyCode}
                inMinorUnit
                price={pricing.subtotalTaxPrice}
                skipConvertCurrency
                skipTaxCalculation
              />
            )}
          </div>
        </div>
      ) : null}
      {pricing.taxType === GeneratedTaxType.SalesTax ? (
        <div className="flex justify-between">
          <div>{t('drawers.review.breakdown.salesTax')}</div>
          <div>
            {updating ? (
              <Skeleton className="h-full w-20" />
            ) : (
              <ProductPrice
                currencyCode={pricing.currencyCode as CurrencyCode}
                inMinorUnit
                price={pricing.subtotalTaxPrice}
                skipConvertCurrency
                skipTaxCalculation
              />
            )}
          </div>
        </div>
      ) : null}
      {paymentReady && (
        <div className="flex flex-col space-y-1">
          {payWithCrypto ? (
            <div className="flex justify-between font-bold">
              <div>{t('product:drawers.review.breakdown.drawDueToday')}</div>
              <ProductPrice
                currencyCode={pricing.currencyCode as CurrencyCode}
                inMinorUnit
                price={pricing.totalPrice}
                skipConvertCurrency
                skipTaxCalculation
              />
            </div>
          ) : (
            <div className="flex justify-between font-bold">
              <div>
                {isPreOrder
                  ? t('product:drawers.review.breakdown.preOrderDueIfAccepted')
                  : t('product:drawers.review.breakdown.drawDueIfWon')}
              </div>
              <div>
                {updating ? (
                  <Skeleton className="h-full w-32" />
                ) : (
                  <ProductPrice
                    currencyCode={pricing.currencyCode as CurrencyCode}
                    inMinorUnit
                    price={pricing.totalPrice}
                    skipConvertCurrency
                    skipTaxCalculation
                  />
                )}
              </div>
            </div>
          )}
          {!updating && pricing.taxType === GeneratedTaxType.Vat && (
            <div className="flex justify-between text-neutral-4">
              <div>
                {t('product:drawers.review.breakdown.vat')} ({pricing.taxRate}%)
              </div>
              <ProductPrice
                currencyCode={pricing.currencyCode as CurrencyCode}
                inMinorUnit
                price={pricing.subtotalTaxPrice}
                skipConvertCurrency
                skipTaxCalculation
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
